import { forwardRef } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '../../lib/cn'

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  /** Строка из словаря frontend/src/shared/search/placeholders.ts. */
  placeholder: string
  /** Очистка поля. Без неё крестик не показывается. */
  onClear?: () => void
  /**
   * Показывать подсказку горячей клавиши. Выключается там, где страница не
   * вешает обработчик «/» — иначе подсказка обещала бы несуществующее.
   */
  hotkey?: boolean
  /** Ширина и отступы задаются страницей: поля стоят в разных полосах. */
  className?: string
  'aria-label'?: string
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

/**
 * Поле поиска каталогов — один вид на все страницы.
 *
 * До унификации каждое поле рисовалось на месте: где-то крестик очистки был,
 * где-то нет; высота была то 36, то 30 пикселей; тёмная тема брала то zinc-900,
 * то zinc-800; подсказка горячей клавиши стояла на пяти страницах из двенадцати
 * и выглядела случайным символом в конце поля.
 *
 * Поведение (пауза, обрезка пробелов, хоткей «/», очистка) живёт в
 * useSearchQuery — этот компонент отвечает только за вид и за то, чтобы
 * подсказка объясняла себя, а не молчала.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onChange, placeholder, onClear, hotkey = true, className, onKeyDown, ...rest },
  ref,
) {
  return (
    <div
      className={cn(
        'flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3',
        'focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-200',
        'dark:border-zinc-700 dark:bg-zinc-900 dark:focus-within:ring-indigo-900',
        className,
      )}
    >
      <Search className="h-4 w-4 shrink-0 text-gray-400 dark:text-zinc-500" />
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label={rest['aria-label'] ?? 'Поиск'}
        className={cn(
          'w-full min-w-0 bg-transparent text-sm text-gray-900 outline-none',
          'placeholder:text-gray-400 dark:text-zinc-100 dark:placeholder:text-zinc-500',
          // Родной крестик Chrome дублировал бы наш и жил своей жизнью.
          '[&::-webkit-search-cancel-button]:appearance-none',
        )}
      />
      {value && onClear ? (
        <button
          type="button"
          onClick={onClear}
          aria-label="Очистить поиск"
          title="Очистить поиск"
          className="shrink-0 text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
      {!value && hotkey ? (
        // Подсказка исчезает, как только в поле что-то есть: там её место
        // занимает крестик, и два значка подряд читались бы как мусор.
        <kbd
          aria-hidden="true"
          title="Клавиша / переводит курсор в поиск"
          className="shrink-0 cursor-default rounded border border-gray-200 px-1 text-2xs text-gray-400 dark:border-zinc-700 dark:text-zinc-500"
        >
          /
        </kbd>
      ) : null}
    </div>
  )
})
